import AndieGenericToolRender from './AndieGenericToolRender';
import { useTranslation } from 'react-i18next';
import { ActionRenderAi } from '@/ai/types/action-render-ai';
import { useEffect, useRef, useState } from 'react';
import { EventAvailableRounded } from '@mui/icons-material';
import { ActionResponseStatusAi } from '@/ai/enum/action-response-status-ai';
import { useQuery } from '@apollo/client';
import { PostDocument } from '@/graphql/generated/post.generated';
import { useAndie } from '@/providers/andie-provider';
import { enqueueSnackbar } from 'notistack';
import { useLocation } from 'react-router-dom';

type ActionResponse = { id: string };
type ActionArgs = { postId: string };

export type AndieSchedulePostProps = ActionRenderAi<ActionResponse, ActionArgs>;

const AndieSchedulePost = (props: AndieSchedulePostProps) => {
    const { t } = useTranslation();
    const { helpers } = useAndie();
    const executingSteps = useRef<number>(0);
    const [postId, setPostId] = useState<string | null>(null);
    const pathname = useLocation().pathname;

    useQuery(PostDocument, {
        skip: !postId,
        ...(postId && { variables: { postId } }),
        onCompleted: (data) => {
            if (data?.post) {
                helpers.updatePost(data.post);
            }
        },
        onError: (e) => {
            enqueueSnackbar(e.message);
        },
        fetchPolicy: 'network-only',
    });

    useEffect(() => {
        if (props.status === ActionResponseStatusAi.EXECUTING) {
            executingSteps.current += 1;
        }

        if (
            props.status === ActionResponseStatusAi.COMPLETE &&
            props.result?.id &&
            executingSteps.current > 0
        ) {
            setPostId(props.result.id);

            const openedPostId = props.args?.postId;
            if (openedPostId && pathname.endsWith('post/' + openedPostId)) {
                helpers.navigateBackFromPost();
                setTimeout(
                    (id: string) => {
                        helpers.navigateToPost(id);
                    },
                    500,
                    openedPostId,
                );
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.status]);

    return (
        <AndieGenericToolRender<ActionResponse, ActionArgs>
            title={() => t('andie.tool.schedule-post.title')}
            icon={<EventAvailableRounded fontSize="small" />}
            loadingMessages={() => [t('andie.tool.schedule-post.loading')]}
            completeMessage={() => t('andie.tool.schedule-post.complete')}
            {...props}
        />
    );
};

export default AndieSchedulePost;
